import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await apiClient.post('/api/auth/login', { email, password });
      const { access_token, user } = response.data;
      
      login(access_token, user);
      
      const redirectPath = user.role === 'admin' ? '/admin' : '/employee';
      navigate(from !== '/' ? from : redirectPath, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Animated Background */}
      <div style={styles.backgroundWrapper}>
        <div style={styles.gradientOrb1} />
        <div style={styles.gradientOrb2} />
        <div style={styles.gradientOrb3} />
        <div style={styles.gridPattern} />
      </div>

      {/* Login Card */}
      <div style={styles.cardWrapper}>
        <div style={styles.card}>
          {/* Logo Section */}
          <div style={styles.logoSection}>
            <div style={styles.logoIcon}>
              <img src="/logo.png" alt="BloomCube AI" style={styles.logoImage} />
            </div>
            <h1 style={styles.logoTitle}>BloomCube AI</h1>
            <p style={styles.logoSubtitle}>Workplace Wellness Platform</p>
          </div>

          {/* Form Section */}
          <form onSubmit={handleSubmit} style={styles.form}>
            {error && (
              <div style={styles.errorBox}>
                <span style={styles.errorIcon}>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <div style={styles.inputGroup}>
              <label style={styles.label}>Email Address</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputIcon}>📧</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={styles.input}
                  placeholder="you@company.com"
                  required
                />
              </div>
            </div>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputIcon}>🔒</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={styles.input}
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>
            
            <button 
              type="submit" 
              style={{
                ...styles.submitBtn,
                opacity: isLoading ? 0.7 : 1,
              }} 
              disabled={isLoading}
            >
              {isLoading ? (
                <span style={styles.loadingSpinner}>⟳</span>
              ) : (
                <>
                  <span>Sign In</span>
                  <span style={styles.btnArrow}>→</span>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div style={styles.cardFooter}>
            <p style={styles.footerText}>
              <img src="/logo.png" alt="" style={styles.footerLogo} /> Promoting wellness, one posture at a time
            </p>
          </div>
        </div>

        {/* Feature Highlights */}
        <div style={styles.features}>
          <div style={styles.featureItem}>
            <span style={styles.featureIcon}>📊</span>
            <span style={styles.featureText}>Real-time posture monitoring</span>
          </div>
          <div style={styles.featureItem}>
            <span style={styles.featureIcon}>🔔</span>
            <span style={styles.featureText}>Smart wellness reminders</span>
          </div>
          <div style={styles.featureItem}>
            <span style={styles.featureIcon}>💪</span>
            <span style={styles.featureText}>Personalized health insights</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)',
    position: 'relative',
    overflow: 'hidden',
    padding: '20px',
  },
  backgroundWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
  },
  gradientOrb1: {
    position: 'absolute',
    width: '600px',
    height: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%)',
    top: '-200px',
    right: '-100px',
    animation: 'float 20s ease-in-out infinite',
  },
  gradientOrb2: {
    position: 'absolute',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(124, 58, 237, 0.25) 0%, transparent 70%)',
    bottom: '-150px',
    left: '-100px',
    animation: 'float 15s ease-in-out infinite reverse',
  },
  gradientOrb3: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, transparent 70%)',
    top: '50%',
    left: '20%',
    animation: 'float 25s ease-in-out infinite',
  },
  gridPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
    `,
    backgroundSize: '50px 50px',
  },
  cardWrapper: {
    position: 'relative',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '32px',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '28px',
    padding: '40px',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.1)',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logoIcon: {
    width: '72px',
    height: '72px',
    margin: '0 auto 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImage: {
    width: '72px',
    height: '72px',
    objectFit: 'contain',
  },
  logoTitle: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1E1B4B',
    margin: 0,
  },
  logoSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    marginTop: '6px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 16px',
    background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
    borderRadius: '12px',
    color: '#DC2626',
    fontSize: '14px',
    fontWeight: 500,
  },
  errorIcon: {
    fontSize: '16px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '16px',
    fontSize: '16px',
    pointerEvents: 'none',
  },
  input: {
    width: '100%',
    padding: '16px 16px 16px 48px',
    border: '2px solid #E5E7EB',
    borderRadius: '14px',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s ease',
    background: '#F9FAFB',
  },
  submitBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '16px 24px',
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '8px',
    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.35)',
    transition: 'all 0.3s ease',
  },
  btnArrow: {
    fontSize: '18px',
    transition: 'transform 0.2s ease',
  },
  loadingSpinner: {
    display: 'inline-block',
    animation: 'spin 1s linear infinite',
    fontSize: '20px',
  },
  cardFooter: {
    marginTop: '28px',
    paddingTop: '20px',
    borderTop: '1px solid #E5E7EB',
    textAlign: 'center',
  },
  footerText: {
    fontSize: '13px',
    color: '#6B7280',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  footerLogo: {
    width: '16px',
    height: '16px',
    objectFit: 'contain',
  },
  features: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
  },
  featureIcon: {
    fontSize: '16px',
  },
  featureText: {
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: 500,
  },
};
